import { Box, Container, Grid, Paper, Stack, Typography } from "@mui/material";
import { Listings } from "../components";
import {
  ConditionalRenderer,
  Header,
  useLanguageTranslator,
  CoverImage,
  StatusTag,
} from "../../../components";
import placeholderImage from "../../../@lib/images/person-placeholder.jpg";
import { gql } from "@apollo/client";
import { Link, useLocation } from "react-router-dom";
import { useApolloQuery } from "../../../hooks";
import { baseURL } from "../../../utils/constants";
import { LeftArrowIcon } from "../../../@lib/icons";
import { AdminActions } from "./adminActions";

const personDetailQuery = gql`
  query person($id: ID!) {
    person(id: $id) {
      id
      firstname
      lastname
      rejectionReason
      country {
        name
      }
      picture {
        source
        originalFilename
      }
      title
      createdAt
      updatedAt
      status
      statusLabel
      careers {
        careerActions {
          action {
            description
          }
          actionDate
          theme {
            title
          }
          country {
            name
          }
        }
        careerPositions {
          position {
            description
          }
          positionDate
          theme {
            title
          }
          country {
            name
          }
        }
        createdAt
        endDate
        id
        institution {
          name
        }
        personId
        role {
          name
        }
        startDate
        updatedAt
      }
    }
  }
`;
function PersonDetail() {
  const { translator } = useLanguageTranslator();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const personId = queryParams.get("id");
  const isAdmin = location?.pathname?.includes("admin");

  const { data, refetch } = useApolloQuery({
    queryDocument: personDetailQuery,
    variables: { id: personId },
  });

  const {
    title = "",
    firstname = "",
    lastname = "",
    picture = null,
    careers = [],
    country = "",
    status,
    statusLabel,
    rejectionReason,
  } = data?.person || {};

  const modifiedCareerPositonsData = careers?.[0]?.careerPositions?.map(
    (item: any) => {
      return {
        description: `${item?.position?.description}`,
        positionDate: item?.positionDate,
      };
    }
  );

  const modifiedCareerActionsData = careers?.[0]?.careerActions?.map(
    (item: any) => {
      return {
        ...item,
        action: {
          ...item?.action,
          description: `${item?.action?.description}`,
        },
      };
    }
  );

  const listingsData = [
    ...(modifiedCareerActionsData || []),
    ...(modifiedCareerPositonsData || []),
  ];

  return (
    <Box pb={4}>
      <Header title={translator("People")} subtitle="" />
      <ConditionalRenderer condition={isAdmin}>
        <Paper component={Container} sx={{ py: 3 }}>
          <Stack direction={{ lg: "row" }} gap={2}>
            <Box>
              <Link
                style={{
                  alignItems: "center",
                  display: "flex",
                  fontSize: "14px",
                  textDecoration: "none",
                  color: "#979da7",
                  fontWeight: "700",
                }}
                to={`/admin/people`}
              >
                <LeftArrowIcon /> {translator("Go back")}
              </Link>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                width: "80%",
              }}
            >
              <Typography variant="h4">
                {translator("Viewing")} {translator("People")}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </ConditionalRenderer>
      <Grid container spacing={2} p={2} bgcolor={"white"}>
        <Grid item xs={12} sm={6} lg={4}>
          <CoverImage
            image={
              picture?.source
                ? `${baseURL}${picture?.source}`
                : placeholderImage
            }
            height={300}
            width={278}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={5}>
          <Typography variant="h5">
            {firstname} {lastname}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: "400" }} mt={1}>
            {careers?.[0]?.role?.name} {translator("Position")}
          </Typography>
          <Typography variant="h5" mt={2}>
            {translator("About")}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: "400" }} mt={1}>
            {title}
          </Typography>
          <ConditionalRenderer condition={isAdmin}>
            <Typography variant="h5" mt={2}>
              {translator("dashboardTableHead.Status")}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: "400" }} mt={1}>
              <StatusTag label={statusLabel} status={status} />
            </Typography>
            <ConditionalRenderer condition={rejectionReason}>
              <Typography variant="h5" fontWeight={300} mt={1}>
                {translator(`Rejection Reason`)}:{rejectionReason}
              </Typography>
            </ConditionalRenderer>
          </ConditionalRenderer>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Typography variant="h5">{translator("Country")}</Typography>
          <Typography variant="h6" sx={{ fontWeight: "400" }} mt={1}>
            {country?.name}
          </Typography>
          <Typography variant="h5" mt={2}>
            {translator("Institution")}
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: "400" }} mt={1}>
            {careers?.[0]?.institution?.name}
          </Typography>
        </Grid>
      </Grid>
      <ConditionalRenderer condition={isAdmin}>
        <AdminActions item={data?.person || {}} refetch={refetch} />
      </ConditionalRenderer>
      <Listings
        title={`${firstname} ${lastname} recent activity`}
        data={listingsData || []}
      />
    </Box>
  );
}

export { PersonDetail };
